// FeatureFlagDeleteAction.jsx

import React from 'react';
import { ReactSVG } from 'react-svg';

// Shared confirmation button component used across the application
// Provides built-in modal handling, accessibility, and styling consistency
import { ConfirmationButton } from '@tmobile/tmo-bps-syncup-web-component-lib';

// Delete icon used inside the confirmation button
import delete_icon from '../../static/assets/icons/action/delete.svg';

/**
 * FeatureFlagDeleteAction
 * -----------------------
 * Encapsulates the delete functionality for a feature flag.
 *
 * Responsibilities:
 * - Display a delete action button
 * - Show a confirmation modal before deletion
 * - Invoke the delete callback with the correct feature identifier
 *
 * This component is intentionally isolated to:
 * - Keep parent components clean and focused on layout
 * - Ensure delete behavior is consistent and reusable
 * - Make future changes (permissions, analytics, UX tweaks) localized
 */
function FeatureFlagDeleteAction({ featureId, onDelete, className }) {
  /**
   * Handles delete confirmation action.
   * This function is triggered only after the user
   * explicitly confirms the delete operation in the modal.
   */
  const handleDelete = () => {
    onDelete(featureId);
  };

  return (
    <ConfirmationButton
      /**
       * Allows the parent component to control spacing, alignment,
       * and visual placement via CSS.
       */
      className={className}
      buttonType="magenta_secondary"
      /**
       * Callback executed after the user confirms deletion.
       * Internally wired to the modal's "accept" action.
       */
      handleDeleteClicked={handleDelete}
      modalMessage={`Are you sure you want to delete feature flag "${featureId}"?`}
      modalTitle="Delete Feature Flag"
      modalAcceptButtonText="Yes,Delete"
      buttonIcon={<ReactSVG src={delete_icon} />}
      tooltip="Delete"
      type="delete"

      /**
       * NOTE:
       * Portal-related props are intentionally omitted.
       * This prevents duplicate modals and ensures the confirmation
       * dialog is managed by the parent layout correctly.
       */
    />
  );
}

export default FeatureFlagDeleteAction;
